import React, { useEffect, useState } from 'react';
import { Row, Form, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { companySelector } from 'slices/company';
import {
  setPickUpLibrary,
  userSelector,
  fetchUserInfo,
  updateUserInfo,
} from 'slices/user';
import { setContactInfo } from 'slices/cart';
import { validateEmailOrPhone } from 'helpers';
import { getBookStores } from 'api/company';
import { CustomSelectComponent } from 'components/CustomSelectComponent';
import { InputCustom } from 'components/input';

const OrderPickup = () => {
  const { t } = useTranslation(['shipping']);
  const dispatch = useDispatch();

  const { name: empresa, direccion_completa, id: bookstoreId } = useSelector(companySelector);
  const { firstName, email, id: userId } = useSelector(userSelector);

  const [options, setOptions] = useState([]);
  const [isDefaultLibraryPicked, setIsDefaultLibraryPicked] = useState(true);
  const [error, setError] = useState({ name: '', email: '' });

  const [contactInfor, setContactInfor] = useState({
    name: firstName || '',
    email: email || '',
  });

  // Fetch librerías
  useEffect(() => {
    const fetchBookStores = async () => {
      try {
        const response = await getBookStores();
        const bookStores = response?.data?.results?.map((store) => ({
          value: store.id,
          label: store.name,
          subtitle: store.direccion,
        }));
        setOptions(bookStores);
      } catch (error) {
        console.error('Error fetching book stores:', error);
      }
    };
    fetchBookStores();
  }, []);

  // Actualizar campos si cambian en Redux
  useEffect(() => {
    setContactInfor((prev) => ({
      ...prev,
      name: firstName || '',
      email: email || '',
    }));
  }, [firstName, email]);

  // Manejar selección de librería
  const handleBookstoreSelection = (storeId) => {
    dispatch(setPickUpLibrary(storeId));
    setIsDefaultLibraryPicked(false);
  };

  const choosePickUpLibrary = () => {
    setIsDefaultLibraryPicked(!isDefaultLibraryPicked);
    if (isDefaultLibraryPicked === false) {
      dispatch(setPickUpLibrary(bookstoreId));
    }
  };

  // Manejo de inputs
  const handleContactInfoChange = (field, value) => {
    setContactInfor((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'email') {
      const validation = validateEmailOrPhone(value);
      if (!validation.isValid) {
        setError((prev) => ({ ...prev, email: t('invalidEmail') }));
      } else {
        setError((prev) => ({ ...prev, email: '' }));
      }
    }

    if (field === 'name' && value.trim()) {
      setError((prev) => ({ ...prev, name: '' }));
    }
  };

  // Actualizar usuario si hay cambios
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      const hasNameChange = contactInfor.name.trim() !== (firstName || '');
      const hasEmailChange = contactInfor.email !== (email || '');

      if (!hasNameChange && !hasEmailChange) return;

      const updateData = {};

      if (hasNameChange) {
        updateData.first_name = contactInfor.name.trim();
      }

      if (hasEmailChange && !error.email) {
        const validation = validateEmailOrPhone(contactInfor.email);
        if (validation.isValid && validation.type === 'email') {
          updateData.email = contactInfor.email;
        }
      }

      if (Object.keys(updateData).length > 0) {
        try {
          await dispatch(updateUserInfo({ data: updateData, id: userId })).unwrap();
          dispatch(fetchUserInfo());
        } catch (err) {
          console.error('Failed to update user info:', err);
        }
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [contactInfor.name, contactInfor.email, firstName, email, error.email, dispatch, userId]);

  // Guardar info de contacto en carrito
  useEffect(() => {
    dispatch(setContactInfo(contactInfor));
  }, [contactInfor, dispatch]);

  return (
    <>
      <h4 className="mx-4 py-3">{t('chooseWherePickup')}</h4>
      <Row className="mx-3">
        <Col lg={6} className="mt-2 d-flex libraryCheck">
          <Form.Check
            type="radio"
            checked={isDefaultLibraryPicked}
            onChange={choosePickUpLibrary}
            name="pickup-selection"
            className="border-highlight"
          />
          <div className="px-2">
            <p>{empresa}</p>
            <p className="m-0">{direccion_completa}</p>
          </div>
        </Col>
        <Col lg={6} className="mt-2 d-flex">
          <Form.Check
            type="radio"
            checked={!isDefaultLibraryPicked}
            onChange={choosePickUpLibrary}
            name="pickup-selection"
            className="border-highlight"
          />
          <Col className="px-2 d-flex flex-column w-100">
            <p>{t('anotherBookstore')}</p>
            <CustomSelectComponent
              handleSelection={handleBookstoreSelection}
              options={options}
              resetValue={isDefaultLibraryPicked}
              isDisabled={isDefaultLibraryPicked}
              dataTestId="another Library"
            />
            {!isDefaultLibraryPicked && (
              <small className="text-muted mt-2">{t('chooseBookstorePickup')}</small>
            )}
          </Col>
        </Col>
      </Row>

      <Row className="mx-3">
        <Col lg={12} className="mt-3 py-2">
          <p className="m-0">{t('contactInfoMessage')}</p>
        </Col>
        <Col lg={6} className="mt-1">
          <InputCustom
            label={`${t('name')}:`}
            type="text"
            value={contactInfor.name}
            onChange={(e) => handleContactInfoChange('name', e.target.value)}
            placeholder={t('enterName')}
            className="styleInput w-100"
          />
          {error.name && <small className="text-danger">{error.name}</small>}
        </Col>
        <Col lg={6} className="mt-1">
          <InputCustom
            label={`${t('email')}:`}
            type="text"
            value={contactInfor.email}
            onChange={(e) => handleContactInfoChange('email', e.target.value)}
            placeholder={t('enterEmail')}
            className="styleInput w-100"
          />
          {error.email && <small className="text-danger">{error.email}</small>}
        </Col>
      </Row>
    </>
  );
};

export default OrderPickup;
